import React, { type FC } from 'react'
import type { PickByValue } from 'utility-types'

import {
    canContainerBeDeleted,
    canContainerBeRestarted,
    canContainerBeStarted,
    canContainerBeStopped,
    type Capability,
    type CybusDetailedCoreContainer,
    type CybusDetailedServiceContainer,
    Translation,
} from '../../../../domain'

import type {
    BulkDeleteServiceContainersUsecase,
    BulkRestartCoreContainersUsecase,
    BulkRestartServiceContainersUsecase,
    BulkStartCoreContainersUsecase,
    BulkStartServiceContainersUsecase,
    BulkStopCoreContainersUsecase,
    BulkStopServiceContainersUsecase,
    Usecases,
} from '../../../../application'

import { DeleteIcon, EnableIcon, StopIcon, UpdateIcon } from '../../common'
import { DetailsActionButton } from '../../Details'
import { useAppUsecases } from '../../State'

type ContainerUsecaseName = keyof PickByValue<
    Usecases,
    | BulkDeleteServiceContainersUsecase
    | BulkRestartServiceContainersUsecase
    | BulkStartServiceContainersUsecase
    | BulkStopServiceContainersUsecase
    | BulkRestartCoreContainersUsecase
    | BulkStartCoreContainersUsecase
    | BulkStopCoreContainersUsecase
>

export const HeaderActions: FC<{
    resource: CybusDetailedServiceContainer | CybusDetailedCoreContainer
    managementCapability: Capability
    deleteUsecaseName?: ContainerUsecaseName
    startUsecaseName: ContainerUsecaseName
    stopUsecaseName: ContainerUsecaseName
    restartUsecaseName: ContainerUsecaseName
}> = ({ resource, managementCapability, deleteUsecaseName, restartUsecaseName, stopUsecaseName, startUsecaseName }) => {
    const usecases = useAppUsecases()

    return (
        <>
            {canContainerBeStarted(resource) && (
                <DetailsActionButton
                    data-testid="start-container-button"
                    text={Translation.START}
                    capabilities={[managementCapability]}
                    onClick={() => usecases[startUsecaseName].selectContainers([resource.id])}
                    startIcon={<EnableIcon />}
                    showText
                />
            )}
            {canContainerBeStopped(resource) && (
                <DetailsActionButton
                    data-testid="stop-container-button"
                    text={Translation.STOP}
                    capabilities={[managementCapability]}
                    onClick={() => usecases[stopUsecaseName].selectContainers([resource.id])}
                    startIcon={<StopIcon />}
                    showText
                />
            )}

            <DetailsActionButton
                data-testid="restart-container-button"
                text={Translation.RESTART}
                disabled={!canContainerBeRestarted(resource)}
                capabilities={[managementCapability]}
                onClick={() => usecases[restartUsecaseName].selectContainers([resource.id])}
                startIcon={<UpdateIcon />}
            />

            {deleteUsecaseName && (
                <DetailsActionButton
                    data-testid="delete-container-button"
                    text={Translation.DELETE}
                    disabled={!canContainerBeDeleted(resource)}
                    capabilities={[managementCapability]}
                    onClick={() => usecases[deleteUsecaseName].selectContainers([resource.id])}
                    startIcon={<DeleteIcon />}
                    variant="dangerOutline"
                />
            )}
        </>
    )
}
